import React, {
  useRef, useState, useCallback, useEffect,
} from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { getResponseError, isSameScalarValue, normalizeArrayValue } from '../../../utils/utils';
import { LOADING_DELAY } from '../../../configs';
import { errorNotification } from '../../Notifications/notificationDecorator';
import Dropdown from '../../../components/base/Dropdown/Dropdown';
import TextInput from '../../../components/fields/TextInput/TextInput';
import { Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';

export default function SelectGeographies(props) {
  const {
    inputProps = null,
    className = '',
    fieldState = {
      error: '',
      touched: false,
    },
    controlId = `text-input-${Math.random() * 10000}`,
    renderOption = null,
    addPlaceholder = false,
    useDefaultNull = false,
    placeholder = '',
    multiple = false,
    customToggleContent = null,
    disabled = false,
    inputClassName = '',
    portalClassName = '',
    variant = '',
    filterName = 'search',
    loadAction,
    optionFormatter = null,
    fallbackOptions = [],
  } = props;
  const {
    error,
    isTouched: touched,
  } = fieldState;
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [toggleFunction, setToggleFunction] = useState(() => () => {});
  const anchorElement = useRef(null);
  const inputRef = useRef(null);
  const mergeOptions = useCallback((loadedOptions = []) => {
    const merged = [];
    [...fallbackOptions, ...loadedOptions].forEach(item => {
      if (!item || typeof item.value === 'undefined' || item.value === null || !String(item.value)) {
        return;
      }
      if (!merged.some(existing => isSameScalarValue(existing.value, item.value))) {
        merged.push(item);
      }
    });
    return merged;
  }, [fallbackOptions]);
  const [options, setOptions] = useState(() => mergeOptions());
  const [search, setSearch] = useState('');
  const [timer, setTimer] = useState(null);
  const isError = Boolean(error);
  const defaultRenderOption = item => (!item.name && item.nameId ? formatMessage({ id: item.nameId }) : item.name || '');
  let renderOptionFunction = defaultRenderOption;
  if (renderOption && typeof renderOption === 'function') {
    renderOptionFunction = renderOption;
  }
  const rawValue = inputProps && (typeof inputProps.value !== 'undefined') ? inputProps.value : multiple ? [] : null;
  const value = multiple ? normalizeArrayValue(rawValue) : rawValue;
  const name = inputProps && (typeof inputProps.name !== 'undefined') ? inputProps.name : '';

  useEffect(() => {
    if (typeof loadAction === 'function') {
      loadOptions();
    }
  }, []);

  const onChange = item => {
    if (inputProps && inputProps.onChange) {
      let changeValue = item.value;
      if (multiple) {
        const newValue = [...value];
        const index = value.findIndex(currentValue => isSameScalarValue(currentValue, item.value));
        if (index > -1) {
          newValue.splice(index, 1);
        } else {
          newValue.push(item.value);
        }
        changeValue = newValue;
      }
      inputProps.onChange({ target: { value: changeValue, name: inputProps.name } });
    }
  };

  const loadOptions = (value = '') => {
    const defaultOptionFormatter = item => ({ value: item.id, name: item.number });
    dispatch(loadAction({ filter: { [filterName]: value } }))
      .then(data => {
        if (!data) { return; }
        setOptions(mergeOptions((data || []).map(optionFormatter || defaultOptionFormatter)));
      })
      .catch(response => {
        errorNotification('Загрузка данных', getResponseError(response));
      });
  };

  const onChangeSearch = event => {
    const { value } = event.target;
    setSearch(value);
    if (!loadAction || !value || value.length < 3) return;
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(setTimeout(() => { loadOptions(value); }, LOADING_DELAY));
  };

  let inputValue = '';
  if (!multiple) {
    const inputOption = options.find(i => isSameScalarValue(i.value, value));
    inputValue = inputOption ? defaultRenderOption(inputOption) : '';
  } else {
    const selectedItems = options
      .filter(item => value.some(currentValue => isSameScalarValue(currentValue, item.value)))
      .map(defaultRenderOption);
    inputValue = selectedItems.join(', ');
  }
  const isSelectedPlaceholder = value => {
    if ((Array.isArray(value) && !value.length) || value === null) {
      return true;
    }
    if (typeof value === 'number' || typeof value === 'string') {
      return !String(value);
    }
    return false;
  };

  return (
    <div className={classNames('form-control-wrapper', className)}>
      <Dropdown
        usePortal
        disabled={disabled}
        setToggleFunction={a => setToggleFunction(() => a)}
      >
        <Dropdown.Toggle className="select-toggle">
          {!customToggleContent || typeof customToggleContent !== 'function'
            ? (
              <TextInput
                {...props}
                readOnly
                inputAfter={(
                  <div id={controlId} className="select-arrow-wrapper">
                    <Icons customIcon className="select-arrow" width={12} height={8} icon="arrow-down" />
                  </div>
              )}
                className={classNames('custom-select', { 'is-invalid': isError && touched }, inputClassName)}
                fieldState={fieldState}
                containerRef={anchorElement}
                inputProps={{
                  name,
                  onChange: () => {},
                  value: inputValue,
                  // onBlur: () => typeof toggleFunction === 'function' && setTimeout(() => toggleFunction(false), 100),
                  // onFocus: () => typeof toggleFunction === 'function' && toggleFunction(true),
                }}
                variant={variant}
              />
            )
            : customToggleContent(props, value, options, anchorElement)}
        </Dropdown.Toggle>
        <Dropdown.Menu
          useResizeContent={false}
          disableClickToggle={multiple}
          rightPadding={32}
          className="select-options-wrapper with-filter"
          anchorElement={anchorElement}
          classNamePortal={classNames('portal-select-options', portalClassName)}
        >
          <TextInput inputProps={{
            onChange: onChangeSearch,
            value: search,
            onClick: e => {
              e.stopPropagation();
              e.preventDefault();
            },
          }}
          />
          <div className="select-options-list">
            {addPlaceholder
              ? (
                <div
                  className={classNames('select-option', { selected: isSelectedPlaceholder(value) })}
                  onClick={() => { onChange({ value: multiple ? [] : useDefaultNull ? null : '' }); }}
                >
                  {renderOptionFunction({ name: placeholder || '' })}
                </div>
              )
              : null}
            {options.map((item, key) => {
              let isSelected = isSameScalarValue(item.value, value);
              if (multiple) {
                isSelected = value.some(currentValue => isSameScalarValue(currentValue, item.value));
              }
              return (
                <div
                  key={key}
                  className={classNames('select-option', { selected: isSelected })}
                  onClick={() => { onChange(item); }}
                >
                  {renderOptionFunction(item)}
                </div>
              );
            })}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
